"use client";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { bookmarkContentAction } from "@/actions/content.action";
import BookmarkIcon from "@/components/icons/bookmark";
import { Button } from "@/components/ui/button";
import type { ApiResponse } from "@/lib/fetch";

interface Props {
  contentId: string;
  initialFavorited: boolean;
}

const FavoriteButtonClient = ({ contentId, initialFavorited }: Props) => {
  const [favorited, setFavorited] = useState(initialFavorited);

  type Snapshot = { favorited: boolean };

  const { mutate, isPending } = useMutation<
    ApiResponse<unknown>,
    Error,
    boolean,
    Snapshot
  >({
    mutationFn: (currentFavorited: boolean) =>
      bookmarkContentAction(contentId, currentFavorited),
    onError: (_err, _vars, snapshot) => {
      if (snapshot) {
        setFavorited(snapshot.favorited);
      }
    },
    onMutate: (currentFavorited) => {
      const snapshot = { favorited };
      setFavorited(!currentFavorited);
      return snapshot;
    },
    onSuccess: (res, _vars, snapshot) => {
      if (!res.isSuccess && snapshot) {
        setFavorited(snapshot.favorited);
      }
    },
  });

  return (
    <Button
      className="group"
      disabled={isPending}
      onClick={() => mutate(favorited)}
      size="icon-sm"
      variant="ghost"
    >
      <BookmarkIcon
        className={
          favorited
            ? "size-6 stroke-[1.5] stroke-brand-blue-500 fill-brand-blue-100"
            : "size-6 stroke-[1.5] stroke-neutral-400 fill-none group-hover:stroke-brand-blue-500 group-hover:fill-brand-blue-100"
        }
      />
    </Button>
  );
};

export { FavoriteButtonClient };
